import {
  Badge,
  Card,
  Cell,
  Column,
  EmptyState,
  Field,
  FieldGrid,
  Row,
  Table,
  TableBody,
  TableContainer,
  TableHeader,
} from '@skupreme/shared/ui';
import type { ReactNode } from 'react';
import type {
  RebuildConnection,
  RebuildSection,
  RebuildV2Settings,
} from '../rebuild-settings-service';

const yesNo = (value: boolean) => (value ? 'Yes' : 'No');

/** A connection's name, or a short id when the new system cannot name it yet. */
export const connectionLabel = ({ connectionId, name }: RebuildConnection) =>
  name ?? `Connection ${connectionId.slice(0, 8)}`;

export type ListRow = { id: string; cells: ReactNode[] };

/**
 * One read-only section as a titled card around a table; a section the
 * server named `unavailable` (`rows` undefined) shows a note instead.
 */
export function ListCard({
  title,
  columns,
  rows,
  emptyMessage,
  action,
}: {
  title: string;
  columns: string[];
  rows: ListRow[] | undefined;
  emptyMessage: string;
  action?: ReactNode;
}) {
  if (!rows) {
    return (
      <Card title={title}>
        <EmptyState
          title="Not available yet"
          description="The new system cannot read this section here yet."
        />
      </Card>
    );
  }
  return (
    <Card title={title} action={action} bodyClassName="sk:p-0">
      <TableContainer>
        <Table aria-label={title}>
          <TableHeader>
            {columns.map((column, index) => (
              <Column key={column} id={column} isRowHeader={index === 0}>
                {column}
              </Column>
            ))}
          </TableHeader>
          <TableBody
            renderEmptyState={() => (
              <p className="sk:px-3 sk:py-6 sk:text-center sk:text-fg-tertiary">
                {emptyMessage}
              </p>
            )}
          >
            {rows.map(row => (
              <Row key={row.id} id={row.id}>
                {row.cells.map((cell, index) => (
                  <Cell key={index}>{cell}</Cell>
                ))}
              </Row>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}

/** EN-4203: what the new system holds for the account, read only. */
export function RebuildSettingsSections({
  settings,
}: {
  settings: RebuildV2Settings;
}) {
  const { qtyRules, routing, carriers, notYet } = settings;
  const unavailable = new Set<RebuildSection>(settings.unavailable ?? []);
  const rowsOf = <T,>(
    section: RebuildSection,
    items: T[] | undefined,
    toRow: (item: T, index: number) => ListRow,
  ) => (unavailable.has(section) || !items ? undefined : items.map(toRow));

  return (
    <div className="sk:flex sk:flex-col sk:gap-4">
      <ListCard
        title="QTY rules"
        columns={[
          'Marketplace',
          'Holdback qty',
          'Publishable qty',
          'Allocate primary',
          'Item rules',
        ]}
        emptyMessage="No QTY rules: every marketplace publishes its full quantity"
        action={
          qtyRules ? (
            <Badge tone="neutral">
              Item rules on all marketplaces:{' '}
              {qtyRules.allMarketplacesItemRuleCount}
            </Badge>
          ) : null
        }
        rows={rowsOf('qtyRules', qtyRules?.connections, rule => ({
          id: rule.connectionId,
          cells: [
            connectionLabel(rule),
            rule.rule?.holdbackQty ?? 0,
            rule.rule?.publishableQty ?? 'No cap',
            yesNo(rule.rule?.allocatePrimary ?? false),
            rule.itemRuleCount,
          ],
        }))}
      />
      <Card title="Order routing">
        <FieldGrid columns={2}>
          <Field label="Routing">
            {routing.automatic ? 'Automatic' : 'Manual'}
          </Field>
          <Field label="Default provider">
            {routing.defaultProvider
              ? connectionLabel(routing.defaultProvider)
              : 'None'}
          </Field>
        </FieldGrid>
      </Card>
      <ListCard
        title="Routing rules"
        columns={['Priority', 'Conditions', 'Target', 'Requires stock']}
        emptyMessage="No routing rules: orders go to the default provider"
        rows={rowsOf('routingRules', routing.rules, rule => ({
          id: rule.id,
          cells: [
            rule.priority,
            <code key="conditions">{JSON.stringify(rule.conditions)}</code>,
            connectionLabel(rule.target),
            yesNo(rule.requireStock),
          ],
        }))}
      />
      <ListCard
        title="Carriers"
        columns={['Account', 'Carrier', 'Billing', 'Default', 'Locations']}
        emptyMessage="No carrier accounts"
        rows={rowsOf('carriers', carriers, (carrier, index) => ({
          id: `${index}:${carrier.carrier}:${carrier.name}`,
          cells: [
            carrier.name,
            carrier.carrier,
            carrier.kind === 'own' ? 'Own account' : 'Third party',
            yesNo(carrier.isDefault),
            carrier.locations.join(', ') || '-',
          ],
        }))}
      />
      <Card title="Not in the new system yet">
        <ul className="sk:list-disc sk:pl-5">
          {notYet.map(item => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
